/**
 * Split View — renders primary content + secondary iframe side by side.
 *
 * The primary panel is the normal <Outlet/>.
 * The secondary panel renders the selected path in an iframe
 * (same-origin, shares auth cookies).
 *
 * Using an iframe avoids the complexity of dual TanStack Router instances.
 * The iframe loads the same app at a different path.
 */

import { Button } from "@/components/ui/button";
import { useSplitViewStore } from "@/lib/split-view";
import { X } from "lucide-react";

interface Props {
	children: React.ReactNode;
}

export function SplitView({ children }: Props) {
	const { isSplit, secondaryPanel, direction, disableSplit } =
		useSplitViewStore();

	if (!isSplit || !secondaryPanel) {
		return <>{children}</>;
	}

	const isHorizontal = direction === "horizontal";

	return (
		<div
			className={`flex flex-1 overflow-hidden ${isHorizontal ? "flex-row" : "flex-col"}`}
		>
			{/* Primary panel */}
			<div className="flex-1 overflow-hidden min-w-0 min-h-0">
				{children}
			</div>

			{/* Divider */}
			<div
				className={`shrink-0 bg-border ${isHorizontal ? "w-px" : "h-px"}`}
			/>

			{/* Secondary panel */}
			<div className="flex-1 overflow-hidden min-w-0 min-h-0 relative">
				{/* Close button */}
				<Button
					variant="outline"
					size="icon"
					onClick={disableSplit}
					className="absolute top-2 right-2 z-10 h-auto w-auto p-1 bg-background/80 backdrop-blur"
				>
					<X className="h-3.5 w-3.5" />
				</Button>
				<iframe
					src={secondaryPanel.path}
					title={secondaryPanel.title}
					className="w-full h-full border-0"
				/>
			</div>
		</div>
	);
}
